import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Stack,
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { getPlatformFailedOutboxEvents } from "api/platformApi";
import { unwrapList } from "api/unwrap";

/**
 * /platform/outbox — cross-tenant failed-outbox-events triage for the
 * whitelabel owner. Useful when a shared downstream (SES, Stripe, an
 * external broker) is misbehaving across multiple tenants at once.
 */

const fmt = (ms) => (ms ? new Date(ms).toLocaleString() : "—");

export const PlatformOutboxPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPlatformFailedOutboxEvents();
      setRows(unwrapList(res));
    } catch (e) {
      setError(e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      `${r.type || ""} ${r.tenantName || ""} ${r.tenantSubdomain || ""} ${r.lastError || ""}`
        .toLowerCase()
        .includes(q),
    );
  }, [rows, filter]);

  // Group counts by tenant for a quick "who's affected" summary above the table.
  const byTenant = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => {
      const key = r.tenantSubdomain || r.tenantName || r.tenantId || "(unknown)";
      m.set(key, (m.get(key) || 0) + 1);
    });
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
  }, [rows]);

  return (
    <Stack
      spacing={3}
      sx={{
        p: { xs: 2, md: 3 },
        backgroundColor: "#f8fafc",
        height: "calc(100vh - 80px)",
        overflowY: "auto",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#0f172a" }}>
            Platform — Failed outbox events
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Most-recent 500 dead-lettered events across every tenant. Useful when a shared downstream is degraded.
          </Typography>
        </Box>
        <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {loading && rows.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
              No failed events. Either you're clean, or platform.admin.enabled isn't set on the backend.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          {byTenant.length > 0 ? (
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  By tenant
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                  Same failure type across many tenants usually means a shared downstream is the problem.
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {byTenant.slice(0, 20).map(([key, count]) => (
                    <Chip
                      key={key}
                      size="small"
                      label={`${key} · ${count}`}
                      sx={{
                        backgroundColor: count >= 10 ? "#dc2626" : count >= 3 ? "#f97316" : "#64748b",
                        color: "#fff",
                        fontWeight: 600,
                      }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          ) : null}

          <TextField
            size="small"
            placeholder="Filter by type / tenant / error"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            sx={{ maxWidth: 480 }}
          />

          <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Processed</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Tenant</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Aggregate</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Retries</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Last error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{fmt(r.processedAtMs)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.tenantName || "—"}</Typography>
                      <Typography variant="caption" color="text.secondary">{r.tenantSubdomain || ""}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: "monospace" }}>{r.type}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {r.aggregateType || "—"}
                        {r.aggregateId ? ` · ${r.aggregateId.slice(0, 8)}` : ""}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{r.retryCount ?? 0}</TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                        {r.lastError || "—"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Stack>
  );
};

export default PlatformOutboxPage;
